import React, { useState, useEffect, useMemo } from 'react';
import { Mic, Palette, Smile, BookOpen, MessageCircle, Sparkles, Pencil, X, Plus, Save, RefreshCw, User, Eye } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { linkedge } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const SENTENCE_STYLE_LABEL = {
  short_punchy: 'Short & punchy',
  medium: 'Medium-length',
  long_narrative: 'Long, narrative',
  mixed: 'Mixed',
};

const POV_PRIMARY_OPTIONS = [
  { value: 'first_person_singular', label: 'First person singular (I, my, me)' },
  { value: 'first_person_plural', label: 'First person plural (we, our, us)' },
  { value: 'third_person', label: 'Third person (no I/we)' },
  { value: 'mixed', label: 'Mixed' },
];

function MetricPill({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg bg-secondary/50 p-3" data-testid="voice-metric">
      <Icon size={14} className="text-primary mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] font-mono-edge text-muted-foreground uppercase">{label}</p>
        <p className="text-sm font-medium truncate" title={value}>{value}</p>
      </div>
    </div>
  );
}

function ToneBalanceBar({ balance }) {
  const personal = Math.max(0, Math.min(100, balance?.personal ?? 50));
  const professional = Math.max(0, Math.min(100, balance?.professional ?? 50));
  const total = personal + professional || 1;
  const personalPct = (personal / total) * 100;
  return (
    <div data-testid="voice-tone-balance">
      <div className="flex justify-between text-[10px] font-mono-edge text-muted-foreground mb-1">
        <span>PERSONAL · {personal}</span>
        <span>{professional} · PROFESSIONAL</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden flex">
        <div className="h-full bg-[hsl(var(--peach))]" style={{ width: `${personalPct}%` }} />
        <div className="h-full bg-primary" style={{ width: `${100 - personalPct}%` }} />
      </div>
    </div>
  );
}

// Small reusable chip-list editor used in edit mode
function ChipListField({ label, items, onChange, placeholder, testIdBase, max = 12 }) {
  const [draft, setDraft] = useState('');
  const safe = Array.isArray(items) ? items : [];
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (safe.some((s) => s.toLowerCase() === v.toLowerCase())) {
      setDraft('');
      return;
    }
    if (safe.length >= max) {
      toast.error(`Max ${max} entries for ${label}`);
      return;
    }
    onChange([...safe, v]);
    setDraft('');
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium">{label}</Label>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {safe.length === 0 && (
          <span className="text-[11px] text-muted-foreground italic">none yet</span>
        )}
        {safe.map((it, i) => (
          <Badge
            key={`${it}-${i}`}
            variant="secondary"
            className="rounded-md gap-1 pr-1 font-normal"
            data-testid={`${testIdBase}-tag-${i}`}
          >
            <span className="max-w-[200px] truncate" title={it}>{it}</span>
            <button
              type="button"
              onClick={() => onChange(safe.filter((_, idx) => idx !== i))}
              className="ml-1 rounded hover:bg-destructive/10 hover:text-destructive p-0.5"
              aria-label={`Remove ${it}`}
              data-testid={`${testIdBase}-remove-${i}`}
            >
              <X size={11} />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="h-8 text-sm"
          data-testid={`${testIdBase}-input`}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={add}
          disabled={!draft.trim()}
          data-testid={`${testIdBase}-add`}
        >
          <Plus size={12} className="mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

function VoiceDisplay({ voice, onEditClick, canEdit }) {
  const sentenceStyleLabel = SENTENCE_STYLE_LABEL[voice.sentence_style] || voice.sentence_style || 'Mixed';
  // Backward compat: prefer habitual_tone, fall back to legacy tone
  const habitualTone = voice.habitual_tone || voice.tone || 'Style snapshot';
  const pov = voice.point_of_view || {};

  const editedBy = voice._last_edited_by;
  const editedAt = voice._last_edited_at;

  return (
    <CardContent className="p-5 space-y-4">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
          <Mic size={16} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono-edge text-primary">YOUR VOICE PROFILE</p>
          <h3 className="text-lg font-semibold mt-0.5" data-testid="voice-tone-headline">{habitualTone}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            This is how we hear you. Voice is permanent — it shapes every post. Tone is what flexes per post.
          </p>
          {editedBy && (
            <p className="text-[10px] font-mono-edge text-muted-foreground mt-1.5" data-testid="voice-edit-meta">
              Last edited by {editedBy.role === 'admin' ? 'admin' : 'owner'}
              {editedAt ? ` · ${new Date(editedAt).toLocaleString()}` : ''}
            </p>
          )}
        </div>
        {canEdit && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onEditClick}
            data-testid="voice-edit-trigger"
          >
            <Pencil size={12} className="mr-1.5" /> Edit voice
          </Button>
        )}
      </div>

      {voice.personality_descriptors && voice.personality_descriptors.length > 0 && (
        <div data-testid="voice-personality-descriptors">
          <p className="text-[10px] font-mono-edge text-muted-foreground mb-1.5">PERSONALITY FINGERPRINT</p>
          <div className="flex flex-wrap gap-1.5">
            {voice.personality_descriptors.map((d, i) => (
              <Badge key={i} className="bg-primary/10 text-primary border border-primary/20 font-mono-edge text-[11px]">
                <Sparkles size={10} className="mr-1" /> {d}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {(pov.primary || pov.first_person_pronoun_frequency || pov.second_person_address) && (
        <div data-testid="voice-pov">
          <p className="text-[10px] font-mono-edge text-muted-foreground mb-1.5">POINT OF VIEW</p>
          <div className="flex flex-wrap gap-1.5">
            {pov.primary && (
              <Badge variant="secondary" className="font-mono-edge text-[11px]">
                <User size={10} className="mr-1" /> {POV_PRIMARY_OPTIONS.find((o) => o.value === pov.primary)?.label || pov.primary}
              </Badge>
            )}
            {pov.first_person_pronoun_frequency && (
              <Badge variant="secondary" className="font-mono-edge text-[11px]">
                I/my frequency: {pov.first_person_pronoun_frequency}
              </Badge>
            )}
            {pov.second_person_address && (
              <Badge variant="secondary" className="font-mono-edge text-[11px]">
                <Eye size={10} className="mr-1" /> &quot;you&quot; address: {pov.second_person_address}
              </Badge>
            )}
            {pov.self_disclosure_level && (
              <Badge variant="secondary" className="font-mono-edge text-[11px]">
                Self-disclosure: {pov.self_disclosure_level}
              </Badge>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MetricPill icon={Palette} label="Sentence style" value={`${sentenceStyleLabel}${voice.avg_words_per_sentence ? ` · ~${voice.avg_words_per_sentence} wpm` : ''}`} />
        <MetricPill icon={Smile} label="Emoji usage" value={voice.emoji_usage} />
        <MetricPill icon={BookOpen} label="Vocabulary" value={voice.vocabulary_level} />
        <MetricPill icon={MessageCircle} label="Jargon" value={voice.jargon_density} />
      </div>

      {voice.argument_style && (
        <div>
          <p className="text-[10px] font-mono-edge text-muted-foreground mb-1.5">ARGUMENT STYLE</p>
          <Badge variant="secondary" className="font-mono-edge text-[11px]">{voice.argument_style.replace(/_/g, ' ')}</Badge>
        </div>
      )}

      {voice.tone_balance && (
        <div>
          <p className="text-[10px] font-mono-edge text-muted-foreground mb-1.5">TONE BALANCE</p>
          <ToneBalanceBar balance={voice.tone_balance} />
        </div>
      )}

      {voice.signature_traits && voice.signature_traits.length > 0 && (
        <div data-testid="voice-signature-traits">
          <p className="text-[10px] font-mono-edge text-muted-foreground mb-1.5">SIGNATURE TRAITS</p>
          <div className="flex flex-wrap gap-1.5">
            {voice.signature_traits.map((t, i) => (
              <Badge key={i} className="bg-primary/10 text-primary border border-primary/20 font-mono-edge text-[11px]">
                <Sparkles size={10} className="mr-1" /> {t}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {voice.openings && voice.openings.length > 0 && (
          <div data-testid="voice-openings">
            <p className="text-[10px] font-mono-edge text-muted-foreground mb-1.5">HOW YOU OPEN POSTS</p>
            <ul className="space-y-1 text-sm">
              {voice.openings.slice(0, 3).map((o, i) => (
                <li key={i} className="italic text-muted-foreground border-l-2 border-primary/40 pl-2">&ldquo;{o}&rdquo;</li>
              ))}
            </ul>
          </div>
        )}
        {voice.closings && voice.closings.length > 0 && (
          <div data-testid="voice-closings">
            <p className="text-[10px] font-mono-edge text-muted-foreground mb-1.5">HOW YOU CLOSE POSTS</p>
            <ul className="space-y-1 text-sm">
              {voice.closings.slice(0, 3).map((c, i) => (
                <li key={i} className="italic text-muted-foreground border-l-2 border-primary/40 pl-2">&ldquo;{c}&rdquo;</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {voice.common_formats && voice.common_formats.length > 0 && (
        <div>
          <p className="text-[10px] font-mono-edge text-muted-foreground mb-1.5">FORMATS YOU FAVOR</p>
          <div className="flex flex-wrap gap-1.5">
            {voice.common_formats.map((f, i) => (
              <Badge key={i} variant="secondary" className="font-mono-edge text-[11px]">{f}</Badge>
            ))}
          </div>
        </div>
      )}

      {voice.themes_already_covered && voice.themes_already_covered.length > 0 && (
        <div data-testid="voice-themes-covered">
          <p className="text-[10px] font-mono-edge text-muted-foreground mb-1.5">
            THEMES YOU&apos;VE ALREADY COVERED — we skip these in the new posts
          </p>
          <div className="flex flex-wrap gap-1.5">
            {voice.themes_already_covered.slice(0, 10).map((t, i) => (
              <Badge key={i} className="bg-[hsl(var(--peach))]/30 text-foreground border border-border font-mono-edge text-[10px]">{t}</Badge>
            ))}
          </div>
        </div>
      )}
    </CardContent>
  );
}

function VoiceEditor({ voice, onCancel, onSaved, analysisId }) {
  // Local draft — only diffs are sent on save
  const initial = useMemo(() => {
    const pov = voice?.point_of_view || {};
    const tb = voice?.tone_balance || {};
    return {
      habitual_tone: voice?.habitual_tone || voice?.tone || '',
      personality_descriptors: Array.isArray(voice?.personality_descriptors) ? [...voice.personality_descriptors] : [],
      point_of_view: {
        primary: pov.primary || '',
        first_person_pronoun_frequency: pov.first_person_pronoun_frequency || '',
        second_person_address: pov.second_person_address || '',
        self_disclosure_level: pov.self_disclosure_level || '',
      },
      argument_style: voice?.argument_style || '',
      sentence_style: voice?.sentence_style || '',
      vocabulary_level: voice?.vocabulary_level || '',
      jargon_density: voice?.jargon_density || '',
      emoji_usage: voice?.emoji_usage || '',
      signature_traits: Array.isArray(voice?.signature_traits) ? [...voice.signature_traits] : [],
      common_formats: Array.isArray(voice?.common_formats) ? [...voice.common_formats] : [],
      openings: Array.isArray(voice?.openings) ? [...voice.openings] : [],
      closings: Array.isArray(voice?.closings) ? [...voice.closings] : [],
      themes_already_covered: Array.isArray(voice?.themes_already_covered) ? [...voice.themes_already_covered] : [],
      tone_balance: {
        personal: typeof tb.personal === 'number' ? tb.personal : 50,
        professional: typeof tb.professional === 'number' ? tb.professional : 50,
      },
    };
  }, [voice]);

  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setPov = (key, value) =>
    setDraft((d) => ({ ...d, point_of_view: { ...d.point_of_view, [key]: value } }));
  const setBalance = (key, value) =>
    setDraft((d) => ({ ...d, tone_balance: { ...d.tone_balance, [key]: value } }));

  const handleSave = async () => {
    if (!analysisId) {
      toast.error('Missing analysis id');
      return;
    }
    // Build payload: drop empty strings; send the whole arrays (lists can be cleared intentionally)
    const payload = {};
    const setIfStr = (k, v) => { if (typeof v === 'string' && v.trim()) payload[k] = v.trim(); };
    setIfStr('habitual_tone', draft.habitual_tone);
    setIfStr('argument_style', draft.argument_style);
    setIfStr('sentence_style', draft.sentence_style);
    setIfStr('vocabulary_level', draft.vocabulary_level);
    setIfStr('jargon_density', draft.jargon_density);
    setIfStr('emoji_usage', draft.emoji_usage);

    payload.personality_descriptors = draft.personality_descriptors;
    payload.signature_traits = draft.signature_traits;
    payload.common_formats = draft.common_formats;
    payload.openings = draft.openings;
    payload.closings = draft.closings;
    payload.themes_already_covered = draft.themes_already_covered;

    const pov = {};
    ['primary', 'first_person_pronoun_frequency', 'second_person_address', 'self_disclosure_level'].forEach((k) => {
      if (draft.point_of_view[k]) pov[k] = draft.point_of_view[k];
    });
    if (Object.keys(pov).length > 0) payload.point_of_view = pov;

    if (
      typeof draft.tone_balance.personal === 'number' ||
      typeof draft.tone_balance.professional === 'number'
    ) {
      payload.tone_balance = {
        personal: Math.max(0, Math.min(100, Math.round(draft.tone_balance.personal || 0))),
        professional: Math.max(0, Math.min(100, Math.round(draft.tone_balance.professional || 0))),
      };
    }

    setSaving(true);
    try {
      const res = await linkedge.updateVoiceProfile(analysisId, payload);
      toast.success('Voice profile updated. Regenerate posts to apply.');
      onSaved?.(res.voice_profile);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update voice'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <CardContent className="p-5 space-y-5">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
          <Mic size={16} />
        </span>
        <div className="flex-1">
          <p className="text-[10px] font-mono-edge text-primary">EDITING VOICE PROFILE</p>
          <h3 className="text-lg font-semibold mt-0.5">Refine the voice fingerprint</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Voice is the permanent fingerprint of how this person writes (POV, personality, rhythm).
            Fix anything the auto-extraction got wrong. Tone (per-post mood) is set elsewhere when regenerating.
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="voice-edit-cancel">
          <X size={14} className="mr-1" /> Cancel
        </Button>
      </div>

      {/* Habitual tone + argument style */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Habitual tone (default mood)</Label>
          <Input
            value={draft.habitual_tone}
            onChange={(e) => setField('habitual_tone', e.target.value)}
            placeholder='e.g. "dry-and-analytical", "warm-but-direct"'
            data-testid="voice-edit-habitual-tone"
          />
          <p className="text-[11px] text-muted-foreground">
            One phrase. This is the mood they default to — not their voice.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Argument style</Label>
          <Select value={draft.argument_style || undefined} onValueChange={(v) => setField('argument_style', v)}>
            <SelectTrigger data-testid="voice-edit-argument-style">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="evidence_led">Evidence-led</SelectItem>
              <SelectItem value="story_led">Story-led</SelectItem>
              <SelectItem value="opinion_led">Opinion-led</SelectItem>
              <SelectItem value="observation_led">Observation-led</SelectItem>
              <SelectItem value="mixed">Mixed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Personality descriptors */}
      <ChipListField
        label="Personality fingerprint (3–5 adjectives)"
        items={draft.personality_descriptors}
        onChange={(v) => setField('personality_descriptors', v)}
        placeholder='e.g. "candid", "dryly skeptical", "executive-grade"'
        testIdBase="voice-edit-personality"
        max={6}
      />

      {/* POV grid */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Primary point of view</Label>
          <Select value={draft.point_of_view.primary || undefined} onValueChange={(v) => setPov('primary', v)}>
            <SelectTrigger data-testid="voice-edit-pov-primary">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {POV_PRIMARY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">First-person pronoun frequency (I, my, me)</Label>
          <Select
            value={draft.point_of_view.first_person_pronoun_frequency || undefined}
            onValueChange={(v) => setPov('first_person_pronoun_frequency', v)}
          >
            <SelectTrigger data-testid="voice-edit-pov-frequency">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Second-person address (&quot;you&quot;)</Label>
          <Select
            value={draft.point_of_view.second_person_address || undefined}
            onValueChange={(v) => setPov('second_person_address', v)}
          >
            <SelectTrigger data-testid="voice-edit-pov-second">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="frequent">Frequent</SelectItem>
              <SelectItem value="occasional">Occasional</SelectItem>
              <SelectItem value="rare">Rare</SelectItem>
              <SelectItem value="never">Never</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Self-disclosure level</Label>
          <Select
            value={draft.point_of_view.self_disclosure_level || undefined}
            onValueChange={(v) => setPov('self_disclosure_level', v)}
          >
            <SelectTrigger data-testid="voice-edit-pov-disclosure">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mechanics row */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Sentence style</Label>
          <Select value={draft.sentence_style || undefined} onValueChange={(v) => setField('sentence_style', v)}>
            <SelectTrigger data-testid="voice-edit-sentence-style">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="short_punchy">Short &amp; punchy</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="long_narrative">Long, narrative</SelectItem>
              <SelectItem value="mixed">Mixed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Vocabulary level</Label>
          <Select value={draft.vocabulary_level || undefined} onValueChange={(v) => setField('vocabulary_level', v)}>
            <SelectTrigger data-testid="voice-edit-vocab">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="casual">Casual</SelectItem>
              <SelectItem value="conversational">Conversational</SelectItem>
              <SelectItem value="mid">Mid</SelectItem>
              <SelectItem value="formal">Formal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Jargon density</Label>
          <Select value={draft.jargon_density || undefined} onValueChange={(v) => setField('jargon_density', v)}>
            <SelectTrigger data-testid="voice-edit-jargon">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Emoji usage</Label>
          <Select value={draft.emoji_usage || undefined} onValueChange={(v) => setField('emoji_usage', v)}>
            <SelectTrigger data-testid="voice-edit-emoji">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="occasional">Occasional</SelectItem>
              <SelectItem value="heavy">Heavy</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tone balance */}
      <div className="space-y-2">
        <Label className="text-xs font-medium">Tone balance (personal vs professional, 0–100)</Label>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono-edge text-muted-foreground w-16">PERSONAL</span>
            <Input
              type="number"
              min={0}
              max={100}
              value={draft.tone_balance.personal}
              onChange={(e) => setBalance('personal', Number(e.target.value))}
              className="h-8 text-sm"
              data-testid="voice-edit-balance-personal"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono-edge text-muted-foreground w-20">PROFESSIONAL</span>
            <Input
              type="number"
              min={0}
              max={100}
              value={draft.tone_balance.professional}
              onChange={(e) => setBalance('professional', Number(e.target.value))}
              className="h-8 text-sm"
              data-testid="voice-edit-balance-professional"
            />
          </div>
        </div>
      </div>

      {/* Signature traits + common formats */}
      <ChipListField
        label="Signature traits (verbal tics, recurring phrases)"
        items={draft.signature_traits}
        onChange={(v) => setField('signature_traits', v)}
        placeholder='e.g. "ends with one-liner observation"'
        testIdBase="voice-edit-traits"
      />
      <ChipListField
        label="Common formats"
        items={draft.common_formats}
        onChange={(v) => setField('common_formats', v)}
        placeholder='e.g. "micro-story then takeaway"'
        testIdBase="voice-edit-formats"
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <ChipListField
          label="Typical openings (example lines)"
          items={draft.openings}
          onChange={(v) => setField('openings', v)}
          placeholder="An example opening line in their voice"
          testIdBase="voice-edit-openings"
          max={6}
        />
        <ChipListField
          label="Typical closings (example lines)"
          items={draft.closings}
          onChange={(v) => setField('closings', v)}
          placeholder="An example closing line in their voice"
          testIdBase="voice-edit-closings"
          max={6}
        />
      </div>

      <ChipListField
        label="Themes already covered (skipped in new posts)"
        items={draft.themes_already_covered}
        onChange={(v) => setField('themes_already_covered', v)}
        placeholder="A topic they have already posted about"
        testIdBase="voice-edit-themes"
        max={20}
      />

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving} data-testid="voice-edit-cancel-bottom">
          Cancel
        </Button>
        <Button type="button" onClick={handleSave} disabled={saving} data-testid="voice-edit-save">
          {saving ? (
            <><RefreshCw size={14} className="mr-1.5 animate-spin" /> Saving…</>
          ) : (
            <><Save size={14} className="mr-1.5" /> Save voice</>
          )}
        </Button>
      </div>
    </CardContent>
  );
}

export default function VoiceProfileCard({ voice, analysisId, onSaved, canEdit = true }) {
  const [editing, setEditing] = useState(false);
  const [localVoice, setLocalVoice] = useState(voice);

  useEffect(() => {
    setLocalVoice(voice);
  }, [voice]);

  if (!localVoice) return null;

  return (
    <Card
      className="border-primary/30 bg-gradient-to-br from-[hsl(var(--mint))]/30 via-card to-[hsl(var(--sky))]/20"
      data-testid="voice-profile-card"
    >
      {editing ? (
        <VoiceEditor
          voice={localVoice}
          analysisId={analysisId}
          onCancel={() => setEditing(false)}
          onSaved={(updated) => {
            setLocalVoice(updated);
            setEditing(false);
            onSaved?.(updated);
          }}
        />
      ) : (
        <VoiceDisplay
          voice={localVoice}
          canEdit={canEdit && !!analysisId}
          onEditClick={() => setEditing(true)}
        />
      )}
    </Card>
  );
}
